import {
	Vue, Component, Watch, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { PickerOptions, PickerResponse } from 'filestack-js';
import { httpClient } from 'utils/http';
import { useRoute } from 'vue-router';
import CustomSelect from 'components/CustomSelect';
import CustomCheckbox from 'components/CustomCheckbox';
import CustomFileInput from 'components/CustomFileInput';
import ThemesPagesTable from '../ThemesPagesTable';
import ThemesCategories from '../ThemeCategories';
import ThemeLayout from '../ThemeLayout';
import ThemeBorderImage from '../ThemeBorderImage';
import ThemesVariant from '../ThemeVariant';
import Template from './template.vue';

@Component({
	components: {
		ThemesPagesTable,
		ThemesCategories,
		ThemeLayout,
		ThemeBorderImage,
		ThemesVariant,
		CustomSelect,
		CustomCheckbox,
		CustomFileInput,
	},
	emits: ['routeLayout', 'routeEditTheme', 'routeThemePages'],
	mixins: [Template],
})
class ThemesEditPageTable extends Vue {
	private singleTheme = {} as Record<string, DB.ThemeModel | number | string | null>;

	private layout: DB.LayoutModel[] = [];

	private loaded = false;

	private route = useRoute();

	private placeHolders = {
		icon: 'Choose a file',
	};

	private visible = false;

	private spinner = false;

	private display = {
		themepage: false,
		themecategory: false,
		themelayout: false,
		themeborderimage: false,
		themevariant: false,
	} as Record<string, boolean>;

	private index = 0;

	private rowHeight = 60;

	private isLoading = false;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	public mounted(): void {
		this.getSingleCategory();
		this.getLayout();
	}

	protected get routeId() {
		return this.route.params.id;
	}

	@Watch('loggedIn')
	private async getSingleCategory(): Promise<Record<string, DB.ThemeModel | number | string | null> | undefined> {
		if (this.loggedIn) {
			// set the loading state
			this.isLoading = true;
			try {
				const resp = await httpClient.get<Record<string, DB.ThemeModel>>(`/api/theme/${this.route.params.id}`);
				this.singleTheme = resp.data;
				// eslint-disable-next-line consistent-return
				return this.singleTheme;
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.isLoading = false;
			}
		}
		return undefined;
	}

	@Watch('loggedIn')
	private async getLayout(): Promise<DB.LayoutModel[] | undefined> {
		if (this.loggedIn) {
			// set the loading state
			this.isLoading = true;
			try {
				const resp = await httpClient.get<DB.LayoutModel[]>(`/api/theme/${this.route.params.id}/layouts`);
				this.layout = resp.data;
				// eslint-disable-next-line consistent-return
				return this.layout;
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.isLoading = false;
			}
		}
		return undefined;
	}

	protected displayPages(property: string): void {
		this.display[property] = true;
	}

	protected setPropertyNull(property: string): void {
		this.singleTheme[property] = null;
	}

	protected checkPropertyNull(property: string): boolean {
		return this.singleTheme[property] === null;
	}

	protected checkPropertyTrue(property: string): boolean {
		return this.singleTheme[property] === 1;
	}

	protected updateProperty(property: string, event: number): void {
		this.singleTheme[property] = event;
	}

	protected editTheme(): Promise<void> | undefined {
		if (this.loggedIn) {
			this.isLoading = true;
			return httpClient.put<DB.ThemeModel>(
				`/api/theme/${this.route.params.id}`,
				{ ...this.singleTheme },
			).then(() => {
				this.$toastSuccess('Theme Edited successfully');
				return undefined;
			})
				.finally(() => {
					this.isLoading = false;
				})
				.catch((error) => {
					this.$toastError(error.message);
				});
		}
		return Promise.resolve(undefined);
	}

	protected routeLayout(id: string): void {
		this.$emit(
			'routeLayout',
			`${id}`,
		);
	}

	protected routeEditTheme(id: string): void {
		this.$emit(
			'routeEditTheme',
			`${id}`,
		);
	}

	protected routeThemePages(id: string): void {
		this.$emit(
			'routeThemePages',
			`${id}`,
		);
	}

	protected updateIcon(): void {
		const options: PickerOptions = {
			fromSources: ['local_file_system'],
			onUploadDone: async (files: PickerResponse) => {
				this.singleTheme.icon = files.filesUploaded[0].url;
				this.placeHolders.icon = files.filesUploaded[0].filename;
			},
		};
		this.$client.picker(options).open();
	}

	protected removeIcon(): void {
		this.singleTheme.icon = '';
	}
}

export default toNative(ThemesEditPageTable);
